import { ApolloClient } from '@apollo/client';
import {
  SortDirection,
  StreamOrderBy,
  StreamStatus,
} from 'api/graphql-global-types';
import {
  GET_STREAMS_WITH_PPV_CLIPS,
  GET_PPV_CLIPS,
  GET_PPV_CLIP_PRESIGNED_URL,
  ADD_PPV_CLIP_NOTE,
} from './queries';
import { AIPpvClipListItem, AIClipRecord, UploadedClipFile } from './types';

type AppApolloClient = ApolloClient<any>;

export interface FetchPpvClipRecordsPageOptions {
  limit?: number;
  offset?: number;
  direction?: 'ASC' | 'DESC';
  /** Sorted by the API so the order holds across pages, not just the rows on screen. */
  orderBy?: StreamOrderBy;
  id?: string;
  slugSearch?: string;
  storeName?: string;
  streamName?: string;
}

export interface FetchPpvClipRecordsPageResult {
  entities: AIPpvClipListItem[];
  total: number;
  offset: number;
  limit: number;
}

interface StreamsWithPpvClipsEntity {
  id: string;
  slug: string;
  name: string;
  scheduleDate: string;
  streamStatus: string;
  store?: {
    id: string;
    slug: string | null;
    firstName?: string | null;
    lastName?: string | null;
    storeDetails?: { storeName: string | null } | null;
  } | null;
  ppvClips: AIClipRecord[] | null;
}

interface StreamsWithPpvClipsQueryResult {
  adminStreams: {
    entities: StreamsWithPpvClipsEntity[];
    total: number;
    offset: number;
    limit: number;
  };
}

const NAME_SEARCH_FETCH_LIMIT = 100;

function getStoreDisplayName(
  store: StreamsWithPpvClipsEntity['store']
): string | undefined {
  if (!store) return undefined;
  if (store.storeDetails?.storeName) return store.storeDetails.storeName;
  const first = store.firstName?.trim();
  const last = store.lastName?.trim();
  if (first || last) return [first, last].filter(Boolean).join(' ');
  return undefined;
}

function mapStreamsToClipListItems(
  streams: StreamsWithPpvClipsEntity[]
): AIPpvClipListItem[] {
  return streams.map((stream) => {
    const records = stream.ppvClips ?? [];
    const [firstRecord] = records;
    const userSlug = firstRecord?.userId || stream.store?.slug || '';
    const streamSlug = firstRecord?.interviewId || stream.slug || '';

    // A stream is reviewable as soon as any recording part produced clips, so a
    // succeeded part outranks a failed one instead of the row reading ERROR.
    const succeeded = records.some((record) => record.status === 'success');
    const failed = records.find((record) => record.status !== 'success');
    const processedAt = records
      .map((record) => record.outputVideoCreatedAt)
      .filter(Boolean)
      .sort()
      .pop();

    return {
      streamId: stream.id,
      storeId: stream.store?.id ?? '',
      userSlug,
      streamSlug,
      streamName: stream.name || '',
      streamCreatedAt: String(stream.scheduleDate || ''),
      streamStatus: String(stream.streamStatus || ''),
      storeName: getStoreDisplayName(stream.store) || undefined,
      recordingCount: records.length,
      clipsGenerated: records.reduce(
        (total, record) => total + (record.clipsGenerated ?? 0),
        0
      ),
      clipStatus: records.length
        ? succeeded
          ? 'success'
          : failed?.status ?? 'success'
        : undefined,
      processedAt,
    };
  });
}

async function fetchStreamsPage(
  client: AppApolloClient,
  input: Record<string, unknown>
): Promise<StreamsWithPpvClipsQueryResult['adminStreams']> {
  const result = await client.query<StreamsWithPpvClipsQueryResult>({
    query: GET_STREAMS_WITH_PPV_CLIPS,
    variables: { input },
    fetchPolicy: 'network-only',
  });

  return (
    result.data?.adminStreams ?? {
      entities: [],
      total: 0,
      offset: (input.offset as number) ?? 0,
      limit: (input.limit as number) ?? 20,
    }
  );
}

function buildBaseInput(options: {
  limit: number;
  offset: number;
  direction: SortDirection;
  orderBy?: StreamOrderBy;
  id?: string;
  slugSearch?: string;
  storeName?: string;
  streamName?: string;
}): Record<string, unknown> {
  return {
    limit: options.limit,
    offset: options.offset,
    direction: options.direction,
    orderBy: options.orderBy ?? StreamOrderBy.ScheduleDateStrict,
    streamStatus: [StreamStatus.Ended],
    price: { from: 0.01, to: 1000000 },
    withCopies: true,
    ...(options.id ? { id: options.id } : {}),
    ...(options.slugSearch ? { storeSlug: options.slugSearch } : {}),
    ...(options.storeName ? { storeName: options.storeName } : {}),
    ...(options.streamName ? { name: options.streamName } : {}),
  };
}

export async function fetchPpvClipRecordsPage(
  client: AppApolloClient,
  options?: FetchPpvClipRecordsPageOptions
): Promise<FetchPpvClipRecordsPageResult> {
  const limit = options?.limit ?? 20;
  const offset = options?.offset ?? 0;
  const direction =
    options?.direction === 'ASC' ? SortDirection.ASC : SortDirection.DESC;

  const result = await fetchStreamsPage(
    client,
    buildBaseInput({
      limit,
      offset,
      direction,
      orderBy: options?.orderBy,
      id: options?.id?.trim(),
      slugSearch: options?.slugSearch?.trim(),
      storeName: options?.storeName?.trim(),
      streamName: options?.streamName?.trim(),
    })
  );

  return {
    entities: mapStreamsToClipListItems(result.entities ?? []),
    total: result.total ?? 0,
    offset,
    limit,
  };
}

export async function fetchAllPpvClipRecordsByNameSearch(
  client: AppApolloClient,
  searchTerm: string,
  direction: 'ASC' | 'DESC' = 'DESC'
): Promise<AIPpvClipListItem[]> {
  const trimmed = searchTerm.trim();
  if (!trimmed) return [];

  const sortDirection =
    direction === 'ASC' ? SortDirection.ASC : SortDirection.DESC;
  const base = {
    limit: NAME_SEARCH_FETCH_LIMIT,
    offset: 0,
    direction: sortDirection,
  };

  const [byStore, byStream] = await Promise.all([
    fetchStreamsPage(client, buildBaseInput({ ...base, storeName: trimmed })),
    fetchStreamsPage(client, buildBaseInput({ ...base, streamName: trimmed })),
  ]);

  const seen = new Set<string>();
  const merged: StreamsWithPpvClipsEntity[] = [];
  for (const entity of [
    ...(byStore.entities ?? []),
    ...(byStream.entities ?? []),
  ]) {
    if (!seen.has(entity.id)) {
      seen.add(entity.id);
      merged.push(entity);
    }
  }

  merged.sort((a, b) => {
    const first = new Date(a.scheduleDate || 0).getTime();
    const second = new Date(b.scheduleDate || 0).getTime();
    return direction === 'ASC' ? first - second : second - first;
  });

  return mapStreamsToClipListItems(merged);
}

export async function fetchPpvClipRecords(
  client: AppApolloClient,
  userSlug: string,
  streamSlug: string
): Promise<AIClipRecord[]> {
  const result = await client.query<{ getPpvClips: AIClipRecord[] }>({
    query: GET_PPV_CLIPS,
    variables: { userSlug, streamSlug },
    fetchPolicy: 'network-only',
  });
  return result.data?.getPpvClips ?? [];
}

export async function getPresignedUrl(
  client: AppApolloClient,
  s3Uri: string
): Promise<string> {
  const result = await client.query<{ getPpvClipPresignedUrl: string }>({
    query: GET_PPV_CLIP_PRESIGNED_URL,
    variables: { s3Uri },
    fetchPolicy: 'network-only',
  });
  return result.data?.getPpvClipPresignedUrl ?? '';
}

/**
 * `streamRecordId` picks the recording the clip belongs to. It is required for interrupted
 * streams: clip filenames repeat across recordings, so without it the note is written to the
 * wrong part. Omit it only for the legacy whole-stream record.
 */
export async function addClipNote(
  client: AppApolloClient,
  userSlug: string,
  streamSlug: string,
  clipName: string,
  note: string,
  streamRecordId?: number | null
): Promise<AIClipRecord['notes']> {
  const result = await client.mutate<{
    addPpvClipNote: Pick<AIClipRecord, 'notes'> | null;
  }>({
    mutation: ADD_PPV_CLIP_NOTE,
    variables: {
      input: {
        userSlug,
        streamSlug,
        clipName,
        note,
        ...(streamRecordId != null ? { streamRecordId } : {}),
      },
    },
  });

  return result.data?.addPpvClipNote?.notes ?? [];
}

export function sortedClipFiles(files: UploadedClipFile[]): UploadedClipFile[] {
  return [...files].sort((a, b) => a.name.localeCompare(b.name));
}

export function isVideoFile(filename: string): boolean {
  return /\.(mp4|mov|webm)$/i.test(filename);
}

export function isThumbnailFile(filename: string): boolean {
  return /\.(jpg|jpeg|png|webp)$/i.test(filename);
}
